import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface ConversionFunnel {
  total_users: number
  limit_reached: number
  upgrade_shown: number
  upgrade_clicked: number
  checkout_started: number
  checkout_abandoned: number
  payment_completed: number
  limit_to_upgrade_rate: number
  upgrade_to_checkout_rate: number
  checkout_to_payment_rate: number
  overall_conversion_rate: number
  dropoff_at_upgrade: number
  dropoff_at_checkout: number
  dropoff_at_payment: number
}

interface HighIntentUser {
  userId: string
  email?: string
  searches: number
  limitHits: number
  upgradeViews: number
  checkoutAttempts: number
  lastActivity: string
  tier: string
  intentScore: number
}

interface PaymentIssues {
  total_failed_payments: number
  total_abandoned_checkouts: number
  payment_method_distribution: Record<string, number>
  top_failure_reasons: Record<string, number>
  recent_failures: any[]
  recent_abandonments: any[]
}

export function RevenueOptimizationDashboard() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [funnel, setFunnel] = useState<ConversionFunnel | null>(null)
  const [highIntentUsers, setHighIntentUsers] = useState<HighIntentUser[]>([])
  const [paymentIssues, setPaymentIssues] = useState<PaymentIssues | null>(null)
  const [selectedDays, setSelectedDays] = useState(7)

  useEffect(() => {
    if (user?.role === 'admin') {
      loadDashboardData()
    }
  }, [user, selectedDays])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      const baseUrl = import.meta.env.VITE_API_URL.replace(/\/$/, '') // Remove trailing slash

      // Load all analytics in parallel
      const [funnelRes, usersRes, paymentsRes] = await Promise.all([
        fetch(`${baseUrl}/analytics/conversion-funnel?days=${selectedDays}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/analytics/high-intent-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/analytics/payment-issues`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      if (funnelRes.ok) {
        const data = await funnelRes.json()
        setFunnel(data.funnel)
      }

      if (usersRes.ok) {
        const data = await usersRes.json()
        setHighIntentUsers(data.highIntentUsers)
      }

      if (paymentsRes.ok) {
        const data = await paymentsRes.json()
        setPaymentIssues(data)
      }

    } catch (error) {
      console.error('Failed to load dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center">Access denied. Admin only.</div>
  }

  if (loading) {
    return <div className="p-8 text-center">Loading revenue insights...</div>
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">💰 Revenue Optimization Dashboard</h1>
        <select
          value={selectedDays}
          onChange={(e) => setSelectedDays(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {/* Conversion Funnel */}
      {funnel && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">🎯 Conversion Funnel</h2>
            <span className="text-sm text-gray-500">Last 7 days</span>
          </div>
          
          <div className="space-y-4">
            {/* Funnel Visualization */}
            <div className="space-y-2">
              <FunnelStep 
                label="Users Hit Limit"
                count={funnel.limit_reached}
                total={funnel.total_users}
                color="blue"
              />
              <FunnelStep 
                label="Clicked Upgrade"
                count={funnel.upgrade_clicked}
                total={funnel.limit_reached}
                color="purple"
                dropoff={funnel.dropoff_at_upgrade}
              />
              <FunnelStep 
                label="Started Checkout"
                count={funnel.checkout_started}
                total={funnel.upgrade_clicked}
                color="indigo"
                dropoff={funnel.dropoff_at_checkout}
              />
              <FunnelStep 
                label="Completed Payment"
                count={funnel.payment_completed}
                total={funnel.checkout_started}
                color="green"
                dropoff={funnel.dropoff_at_payment}
              />
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t">
              <MetricCard 
                label="Overall Conversion"
                value={`${funnel.overall_conversion_rate}%`}
                trend={funnel.overall_conversion_rate > 5 ? 'up' : 'down'}
              />
              <MetricCard 
                label="Limit → Upgrade"
                value={`${funnel.limit_to_upgrade_rate}%`}
                trend={funnel.limit_to_upgrade_rate > 20 ? 'up' : 'down'}
              />
              <MetricCard 
                label="Upgrade → Checkout"
                value={`${funnel.upgrade_to_checkout_rate}%`}
                trend={funnel.upgrade_to_checkout_rate > 50 ? 'up' : 'down'}
              />
              <MetricCard 
                label="Checkout → Payment"
                value={`${funnel.checkout_to_payment_rate}%`}
                trend={funnel.checkout_to_payment_rate > 70 ? 'up' : 'down'}
              />
            </div>
          </div>
        </div>
      )}

      {/* High-Intent Users */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">🔥 High-Intent Users (Ready to Upgrade)</h2>
        
        {highIntentUsers.length === 0 ? (
          <p className="text-gray-600">No high-intent users found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Searches</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Limit Hits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upgrade Views</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checkout Attempts</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {highIntentUsers.slice(0, 20).map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{user.email || user.userId}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        user.intentScore >= 100 ? 'bg-red-100 text-red-800' :
                        user.intentScore >= 70 ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.intentScore}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.searches}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.limitHits}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.upgradeViews}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.checkoutAttempts}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(user.lastActivity).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        Send Email
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Issues */}
      {paymentIssues && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">⚠️ Payment Issues</h2>
          
          <div className="grid grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Failed Payments</h3>
              <p className="text-3xl font-bold text-red-600">{paymentIssues.total_failed_payments}</p>
              <div className="mt-2 text-sm text-gray-600">
                {Object.entries(paymentIssues.top_failure_reasons).map(([reason, count]) => (
                  <div key={reason} className="flex justify-between">
                    <span>{reason}:</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Abandoned Checkouts</h3>
              <p className="text-3xl font-bold text-orange-600">{paymentIssues.total_abandoned_checkouts}</p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment Methods</h3>
              <div className="space-y-1 text-sm">
                {Object.entries(paymentIssues.payment_method_distribution).map(([method, count]) => (
                  <div key={method} className="flex justify-between">
                    <span>{method}:</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper Components
function FunnelStep({ label, count, total, color, dropoff }: any) {
  const percentage = total > 0 ? Math.min((count / total) * 100, 100) : 0
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-600">{count} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-8 relative">
        <div 
          className={`bg-${color}-500 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold`}
          style={{ width: `${percentage}%` }}
        >
          {count}
        </div>
      </div>
      {dropoff !== undefined && dropoff > 0 && (
        <p className="text-xs text-red-600 mt-1">⚠️ {dropoff} users dropped off</p>
      )}
    </div>
  )
}

function MetricCard({ label, value, trend }: any) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {trend === 'up' && <span className="text-green-600">↑</span>}
        {trend === 'down' && <span className="text-red-600">↓</span>}
      </div>
    </div>
  )
}
